import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { NavLink as RouterLink } from 'react-router-dom';
import {
  Box,
  List,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Collapse,
  TextField,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ROUTE_PATH from '../../../config/routes.config';
import { GREY, PRIMARY } from '../../../theme/theme-colors';
import Iconify from '../../common/iconify.component';
import useAccess from '../../../hooks/access.hook';

const StyledNavItem = styled((props) => (
  <ListItemButton disableGutters {...props} />
))(({ theme }) => ({
  ...theme.typography.body2,
  height: 48,
  position: 'relative',
  textTransform: 'capitalize',
  color: 'white',
}));

const StyledNavItemIcon = styled(ListItemIcon)({
  width: 24,
  height: 24,
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 18,
});

const NavItem = ({ title, path, icon, subNavItem }) => (
  <StyledNavItem
    component={RouterLink}
    to={path}
    sx={{
      '&:hover': {
        color: 'white',
        bgcolor: GREY[900],
      },
      '&.active': {
        color: 'white',
        bgcolor: PRIMARY.main,
        fontWeight: 'fontWeightBold',
      },
      bgcolor: subNavItem ? GREY[800] : 'inherit',
      pl: subNavItem ? 1 : 0,
    }}
  >
    <StyledNavItemIcon>{icon}</StyledNavItemIcon>
    <ListItemText disableTypography primary={title} />
  </StyledNavItem>
);

NavItem.propTypes = {
  title: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
  icon: PropTypes.element.isRequired,
  subNavItem: PropTypes.bool,
};

NavItem.defaultProps = {
  subNavItem: false,
};

const CollapsibleNavSection = ({
  open: controlledOpen,
  defaultOpen,
  title,
  icon,
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  useEffect(() => {
    if (typeof controlledOpen === 'boolean') {
      setOpen(controlledOpen);
    }
  }, [controlledOpen]);

  const handleToggle = () => {
    setOpen(!open);
  };

  return (
    <>
      <StyledNavItem onClick={handleToggle}>
        <StyledNavItemIcon>{icon}</StyledNavItemIcon>
        <ListItemText disableTypography primary={title} />
        {open ? (
          <Iconify icon="ic:round-expand-less" width={25} />
        ) : (
          <Iconify icon="ic:round-expand-more" width={25} />
        )}
      </StyledNavItem>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List disablePadding>{children}</List>
      </Collapse>
    </>
  );
};

CollapsibleNavSection.propTypes = {
  open: PropTypes.bool,
  defaultOpen: PropTypes.bool,
  title: PropTypes.string.isRequired,
  icon: PropTypes.element.isRequired,
  children: PropTypes.node.isRequired,
};

CollapsibleNavSection.defaultProps = {
  open: undefined,
  defaultOpen: false,
};

const NavItems = () => {
  const {
    hasAccess,
    hasAdminAccess,
    hasSuperAdminAccess,
    hasContentOwnerAccess,
  } = useAccess();
  const [searchTerm, setSearchTerm] = useState('');

  const routeArr = [
    {
      name: 'Dashboard',
      isAccess: true,
      path: ROUTE_PATH.DASHBOARD,
      icon: <Iconify icon="ic:round-dashboard" />,
    },
    {
      name: 'Manage Videos',
      isAccess:
        hasAccess('video_library') ||
        hasAccess('series_library') ||
        hasAccess('manage_descriptions') ||
        hasAccess('license_management'),
      path: '',
      icon: <Iconify icon="material-symbols:bookmark-manager-outline" />,
      autoOpen: false,
      childrens: [
        {
          name: 'Video Library',
          isAccess: hasAccess('video_library'),
          path: ROUTE_PATH.VIDEOS,
          icon: <Iconify icon="octicon:video-16" />,
        },
        {
          name: 'Series Library',
          isAccess: hasAccess('series_library'),
          path: ROUTE_PATH.SERIES,
          icon: <Iconify icon="bxs:videos" />,
        },
        {
          name: 'Manage Descriptions',
          isAccess: hasAccess('manage_descriptions'),
          path: ROUTE_PATH.CONTENT_MANAGE_DESCRIPTIONS,
          icon: <Iconify icon="fluent:text-description-16-filled" />,
        },
        {
          name: 'License Management',
          isAccess: hasAccess('license_management'),
          path: ROUTE_PATH.LICENSE_MANAGEMENT,
          icon: <Iconify icon="fluent:text-description-16-filled" />,
        },
      ],
    },
    {
      name: 'Encode Videos',
      isAccess: hasAdminAccess(),
      path: '',
      icon: <Iconify icon="material-symbols:compress-rounded" />,
      autoOpen: false,
      childrens: [
        {
          name: 'HLS Encoder (Legacy)',
          isAccess: hasAdminAccess(),
          path: ROUTE_PATH.HLS_TRANSCODER_LEGACY,
          icon: (
            <Iconify icon="fluent:arrow-clockwise-dashes-settings-16-filled" />
          ),
        },
        {
          name: 'HLS Encoder (Object)',
          isAccess: hasAdminAccess(),
          path: ROUTE_PATH.HLS_TRANSCODER_OBJECT,
          icon: (
            <Iconify icon="fluent:arrow-clockwise-dashes-settings-16-filled" />
          ),
        },
        {
          name: 'Filmhub',
          isAccess: hasAdminAccess(),
          path: ROUTE_PATH.CONTENT_SOURCES,
          icon: <Iconify icon="tdesign:film-filled" />,
        },
      ],
    },
    {
      name: 'Organize Videos',
      isAccess:
        hasAccess('genres') ||
        hasAccess('playlists') ||
        hasAccess('collections'),
      path: '',
      icon: <Iconify icon="fluent:organization-16-regular" />,
      autoOpen: false,
      childrens: [
        {
          name: 'Genres',
          isAccess: hasAccess('genres'),
          path: ROUTE_PATH.CONTENT_GENRES,
          icon: <Iconify icon="teenyicons:attachment-outline" />,
        },
        {
          name: 'Playlists',
          isAccess: hasAccess('playlists'),
          path: ROUTE_PATH.CONTENT_ALL_PLAYLISTS,
          icon: <Iconify icon="mingcute:playlist-line" />,
        },
        {
          name: 'Collections',
          isAccess: hasAccess('collections'),
          path: ROUTE_PATH.CONTENT_COLLECTIONS,
          icon: <Iconify icon="bytesize:folder" />,
        },
      ],
    },
    {
      name: 'Schedule Videos',
      isAccess:
        hasAccess('rule_based_scheduling') ||
        hasAccess('preview_channels') ||
        hasAccess('rules_library') ||
        (!hasContentOwnerAccess() && hasAccess('manual_assignment')),
      path: '',
      icon: <Iconify icon="material-symbols:schedule-outline" />,
      autoOpen: false,
      childrens: [
        {
          name: 'Rule-Based Scheduling',
          isAccess: hasAccess('rule_based_scheduling'),
          path: ROUTE_PATH.CONTENT_SCHEDULING,
          icon: <Iconify icon="material-symbols:schedule-outline-rounded" />,
        },
        {
          name: 'Preview Channels',
          isAccess: hasAccess('preview_channels'),
          path: ROUTE_PATH.CHANNEL_PREVIEW,
          icon: <Iconify icon="ic:round-preview" />,
        },
        {
          name: 'Rules Library',
          isAccess: hasAccess('rules_library'),
          path: ROUTE_PATH.MANAGE_RULES,
          icon: <Iconify icon="bx:edit" />,
        },
        {
          name: 'Manual Assignment',
          isAccess: !hasContentOwnerAccess() && hasAccess('manual_assignment'),
          path: ROUTE_PATH.MANAGE_SCHEDULE,
          icon: <Iconify icon="akar-icons:schedule" />,
        },
      ],
    },
    {
      name: 'View Schedule',
      isAccess: hasContentOwnerAccess() && hasAccess('manual_assignment'),
      path: ROUTE_PATH.MANAGE_SCHEDULE,
      icon: <Iconify icon="akar-icons:schedule" />,
    },
    {
      name: 'Manage Channels',
      isAccess:
        hasAccess('channel_settings') ||
        hasAccess('global_assets') ||
        hasAccess('channel_packager'),
      path: '',
      icon: <Iconify icon="carbon:gui-management" />,
      autoOpen: false,
      childrens: [
        {
          name: 'Channel Settings',
          isAccess: hasAccess('channel_settings'),
          path: ROUTE_PATH.CONTENT_ALL_CHANNELS,
          icon: <Iconify icon="ic:twotone-checklist" />,
        },
        {
          name: 'Global Assets',
          isAccess: hasAccess('global_assets'),
          path: ROUTE_PATH.CHANNEL_DEFAULT_CONTENT,
          icon: <Iconify icon="healthicons:default-outline" />,
        },
        {
          name: 'Channel Packager',
          isAccess: hasAccess('channel_packager'),
          path: ROUTE_PATH.CHANNEL_PACKAGER,
          icon: (
            <Iconify icon="material-symbols-light:package-2-outline-sharp" />
          ),
        },
      ],
    },
    {
      name: 'Manage Users',
      isAccess:
        hasAccess('all_users') ||
        hasAccess('all_subscribers') ||
        hasAccess('promo_code_categories') ||
        hasAccess('promo_codes'),
      path: '',
      icon: <Iconify icon="ci:users" />,
      autoOpen: false,
      childrens: [
        {
          name: 'All Users',
          isAccess: hasAccess('all_users'),
          path: ROUTE_PATH.SUBSCRIBERS,
          icon: <Iconify icon="ci:users" />,
        },
        {
          name: 'All Subscribers',
          isAccess: hasAccess('all_subscribers'),
          path: ROUTE_PATH.BILLING_SUBSCRIPTIONS,
          icon: <Iconify icon="ic:outline-receipt-long" />,
        },
        {
          name: 'Promo Codes',
          isAccess: hasAccess('promo_codes'),
          path: ROUTE_PATH.COUPONS_CONFIG,
          icon: <Iconify icon="mdi:coupon" />,
        },
        {
          name: 'Promo Code Categories',
          isAccess: hasAccess('promo_code_categories'),
          path: ROUTE_PATH.COUPONS_CATEGORIES_SETTINGS,
          icon: <Iconify icon="mdi:coupon" />,
        },
      ],
    },
    {
      name: 'Manage Distributors',
      isAccess: hasAccess('distributors'),
      path: '',
      icon: <Iconify icon="fa6-solid:users-gear" />,
      autoOpen: false,
      childrens: [
        {
          name: 'Distributors',
          isAccess: hasAccess('distributors'),
          path: ROUTE_PATH.CONTENT_OWNERS,
          icon: <Iconify icon="mdi:users-check" />,
        },
      ],
    },
    {
      name: 'Advertising',
      isAccess: hasAccess('advertising'),
      path: '',
      icon: <Iconify icon="tabler:ad" />,
      autoOpen: false,
      childrens: [
        {
          name: 'Tag Library',
          isAccess: hasAccess('advertising'),
          path: ROUTE_PATH.ADVERTISERS_TAG_LIBRARY,
          icon: <Iconify icon="mdi:tag-outline" />,
        },
        {
          name: 'Assign Tags',
          isAccess: hasAccess('advertising'),
          path: ROUTE_PATH.ADVERTISERS_TAG_ASSIGN,
          icon: <Iconify icon="mdi:tag-plus-outline" />,
        },
        {
          name: 'Whale TV Settings',
          isAccess: hasAccess('advertising'),
          path: ROUTE_PATH.OZYPLAY_SETTINGS,
          icon: <Iconify icon="mdi:tag-plus-outline" />,
        },
        {
          name: 'Deploy Tags',
          isAccess: hasAccess('advertising'),
          path: ROUTE_PATH.ADVERTISERS_TAG_DEPLOYED,
          icon: (
            <Iconify icon="material-symbols-light:list-alt-check-outline" />
          ),
        },
      ],
    },
    {
      name: 'Data Management',
      isAccess: hasAccess('import') || hasAccess('export'),
      path: '',
      icon: <Iconify icon="carbon:import-export" />,
      autoOpen: false,
      childrens: [
        {
          name: 'Import / Export',
          isAccess: hasAccess('import') || hasAccess('export'),
          path: ROUTE_PATH.IMPORT,
          icon: <Iconify icon="carbon:import-export" />,
        },
      ],
    },
    {
      name: 'Analytics',
      isAccess: hasAccess('analytics'),
      path: '',
      icon: <Iconify icon="ic:outline-analytics" />,
      autoOpen: false,
      childrens: [
        {
          name: 'Overview',
          isAccess: true,
          path: ROUTE_PATH.REPORTS_OVERVIEW,
          icon: <Iconify icon="ic:outline-analytics" />,
        },
        {
          name: 'Web Subscriber Analytics',
          isAccess: true,
          path: ROUTE_PATH.WEBSUBSCRIBER_ANALYTICS,
          icon: <Iconify icon="ic:outline-analytics" />,
        },
        {
          name: 'Realtime Apps',
          isAccess: true,
          path: ROUTE_PATH.REALTIME_DASHBOARD,
          icon: <Iconify icon="tabler:activity-heartbeat" />,
        },
        {
          name: 'Realtime Users',
          isAccess: true,
          path: ROUTE_PATH.REPORTS_LIVE_USERS,
          icon: <Iconify icon="ic:round-live-tv" />,
        },
        {
          name: 'Active Subscribers',
          isAccess: true,
          path: ROUTE_PATH.REPORTS_ACTIVE_SUBSCRIBERS,
          icon: <Iconify icon="ic:outline-analytics" />,
        },
        {
          name: 'Trial Period Subscribers',
          isAccess: true,
          path: ROUTE_PATH.REPORTS_TRIAL_SUBSCRIBERS,
          icon: <Iconify icon="ic:outline-analytics" />,
        },
        {
          name: 'Users Count',
          isAccess: true,
          path: ROUTE_PATH.REPORTS_CHANNEL_ALL_USERS,
          icon: <Iconify icon="ic:outline-analytics" />,
        },
        {
          name: 'Views by App',
          isAccess: true,
          path: ROUTE_PATH.CONTENT_CHANNELS,
          icon: <Iconify icon="ic:twotone-checklist" />,
        },
        {
          name: 'Views by Video',
          isAccess: true,
          path: ROUTE_PATH.CONTENT_VIDEOS,
          icon: <Iconify icon="octicon:video-16" />,
        },
        {
          name: 'Views by Playlist',
          isAccess: true,
          path: ROUTE_PATH.CONTENT_PLAYLISTS,
          icon: <Iconify icon="ic:round-playlist-add" />,
        },
        {
          name: 'Views by Session (Video)',
          isAccess: true,
          path: ROUTE_PATH.REPORTS_SESSIONS,
          icon: <Iconify icon="ic:outline-list-alt" />,
        },
        {
          name: 'Views by Session (App)',
          isAccess: true,
          path: ROUTE_PATH.REPORTS_APP_SESSIONS,
          icon: <Iconify icon="ion:analytics-sharp" />,
        },
        {
          name: 'Promo Code Usage',
          isAccess: true,
          path: ROUTE_PATH.REPORTS_PROMO_CODE,
          icon: <Iconify icon="mdi:coupon" />,
        },
        {
          name: 'Migration Status',
          isAccess: true,
          path: ROUTE_PATH.REPORTS_MIGRATION_STATUS,
          icon: <Iconify icon="ic:outline-analytics" />,
        },
        {
          name: 'Migration Users',
          isAccess: true,
          path: ROUTE_PATH.REPORTS_MIGRATION_USERS,
          icon: <Iconify icon="ic:outline-analytics" />,
        },
        {
          name: 'Failed Subscription Logs',
          isAccess: hasSuperAdminAccess(),
          path: ROUTE_PATH.FAILED_SUBSCRIPTIONS,
          icon: <Iconify icon="ic:outline-receipt-long" />,
        },
        {
          name: 'Cancel Subscription Requests',
          isAccess: hasSuperAdminAccess(),
          path: ROUTE_PATH.CANCEL_SUBSCRIPTION_REQUESTS,
          icon: <Iconify icon="ic:outline-receipt-long" />,
        },
      ],
    },
    {
      name: 'Settings',
      isAccess:
        hasAccess('workspaces') ||
        hasAccess('mail_server') ||
        hasAccess('email_templates') ||
        hasAccess('channel_genres') ||
        hasAccess('blacklist_sources') ||
        hasAccess('system_health'),
      path: '',
      icon: <Iconify icon="ic:outline-settings" />,
      autoOpen: false,
      childrens: [
        {
          name: 'User Permissions',
          isAccess: hasSuperAdminAccess(),
          path: ROUTE_PATH.ADMIN_USERS,
          icon: <Iconify icon="mdi:users-check" />,
        },
        {
          name: 'Workspaces',
          isAccess: hasAccess('workspaces'),
          path: ROUTE_PATH.WORKSPACE_SETTINGS,
          icon: <Iconify icon="carbon:workspace" />,
        },
        {
          name: 'Mail Server',
          isAccess: hasAccess('mail_server'),
          path: ROUTE_PATH.SMTP_SETTINGS,
          icon: <Iconify icon="ic:outline-mail" />,
        },
        {
          name: 'Email Templates',
          isAccess: hasAccess('email_templates'),
          path: ROUTE_PATH.EMAIL_TEMPLATES,
          icon: <Iconify icon="mdi:email-edit-outline" />,
        },
        {
          name: 'Channel Genres',
          isAccess: hasAccess('channel_genres'),
          path: ROUTE_PATH.CONTENT_CHANNEL_GENRES,
          icon: <Iconify icon="teenyicons:attachment-outline" />,
        },
        {
          name: 'Blacklist Sources',
          isAccess: hasAccess('blacklist_sources'),
          path: ROUTE_PATH.BLACKLIST_SOURCES_SETTINGS,
          icon: <Iconify icon="material-symbols:block" />,
        },
        {
          name: 'System Health',
          isAccess: hasAccess('system_health'),
          path: ROUTE_PATH.SYSTEM_HEALTH_SETTINGS,
          icon: <Iconify icon="ix:health-filled" />,
        },
      ],
    },
  ];

  const filteredRoutes = routeArr
    .filter((section) => section.isAccess) // only allowed sections
    .map((section) => {
      if (!searchTerm) {
        return {
          ...section,
          childrens: (section.childrens || []).filter((c) => c.isAccess),
          autoOpen: false, // default
        };
      }

      const matchesParent = section.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

      const matchedChildren = (section.childrens || []).filter(
        (child) =>
          child.isAccess &&
          child.name.toLowerCase().includes(searchTerm.toLowerCase())
      );

      if (matchesParent) {
        return {
          ...section,
          childrens: (section.childrens || []).filter((c) => c.isAccess),
          autoOpen: true, // expand if parent matches
        };
      }

      if (matchedChildren.length > 0) {
        return { ...section, childrens: matchedChildren, autoOpen: true }; // expand if children match
      }

      return null; // no match at all
    })
    .filter(Boolean);

  return (
    <Box>
      <Box mt={2} mb={1} px={2}>
        <TextField
          placeholder="Search menu..."
          size="small"
          fullWidth
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            input: { color: '#FFFFFF' },
            '& .MuiInputBase-input::placeholder': {
              color: '#FFFFFF',
              opacity: 0.7,
            },
          }}
        />
      </Box>
      <List disablePadding sx={{ p: 1 }}>
        {filteredRoutes.map((section) => {
          if (section.isAccess) {
            if (section.childrens && section.childrens.length > 0) {
              return (
                <CollapsibleNavSection
                  open={section.autoOpen}
                  title={section.name}
                  icon={section.icon}
                >
                  {section.childrens
                    .filter((child) => child.isAccess)
                    .map((child) => (
                      <NavItem
                        title={child.name}
                        path={child.path}
                        icon={child.icon}
                        subNavItem
                      />
                    ))}
                </CollapsibleNavSection>
              );
            }
            return (
              <NavItem
                title={section.name}
                path={section.path}
                icon={section.icon}
              />
            );
          }
          return null;
        })}
      </List>
    </Box>
  );
};

export default NavItems;
